import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";

const RulesTab = ({
    rulesApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onReorder,
}) => {
    const [orderedRules, setOrderedRules] = useState([]);
    const [draggingRuleId, setDraggingRuleId] = useState(null);
    const [reordering, setReordering] = useState(false);

    useEffect(() => {
        setOrderedRules(Array.isArray(rulesApi.data) ? [...rulesApi.data] : []);
    }, [rulesApi.data]);

    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [sortBy, setSortBy] = useState("priority_asc");

    const filteredRules = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = orderedRules.filter((r) => {
            if (activeFilter === "active" && !r.active) return false;
            if (activeFilter === "inactive" && r.active) return false;
            if (!q) return true;
            const tx = Array.isArray(r.transaction_types) ? r.transaction_types.join(",") : (r.transaction_type || "");
            return String(r.name || "").toLowerCase().includes(q)
                || String(tx).toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "name_asc") return String(a.name || "").localeCompare(String(b.name || ""), "fr");
            if (sortBy === "updated_desc") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            return Number(a.priority || 0) - Number(b.priority || 0);
        });
        return rows;
    }, [orderedRules, search, activeFilter, sortBy]);

    const pageRaw = pages[3] || 0;
    const maxPage = Math.max(0, Math.ceil(filteredRules.length / ROWS_PER_PAGE) - 1);
    const page = Math.min(pageRaw, maxPage);

    const paginated = useMemo(
        () => filteredRules.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
        [filteredRules, page]
    );

    const handleDragStart = (ruleId) => (e) => {
        setDraggingRuleId(ruleId);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (targetRuleId) => (e) => {
        e.preventDefault();
        if (!draggingRuleId || draggingRuleId === targetRuleId) return;
        setOrderedRules((prev) => {
            const from = prev.findIndex((r) => r.id === draggingRuleId);
            const to = prev.findIndex((r) => r.id === targetRuleId);
            if (from < 0 || to < 0 || from === to) return prev;
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next.map((r, idx) => ({ ...r, priority: idx }));
        });
    };

    const handleDrop = async () => {
        if (!draggingRuleId || typeof onReorder !== "function") {
            setDraggingRuleId(null);
            return;
        }
        try {
            setReordering(true);
            await onReorder(orderedRules.map((r) => r.id));
        } catch (err) {
            console.error("Failed to reorder rules", err);
            setOrderedRules(Array.isArray(rulesApi.data) ? [...rulesApi.data] : []);
        } finally {
            setReordering(false);
            setDraggingRuleId(null);
        }
    };

    const handleDragEnd = () => {
        setDraggingRuleId(null);
    };

    if (rulesApi.error) return <ErrorState error={rulesApi.error} onRetry={() => rulesApi.refetch()} />;
    if (rulesApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (!Array.isArray(rulesApi.data) || rulesApi.data.length === 0) {
        return <EmptyState message="Aucune règle configurée" color={SECTION_COLORS.rules.main} />;
    }

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher une règle (nom, transaction)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "priority_asc", label: "Priorité (croissante)" },
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "updated_desc", label: "Mise à jour (récent)" },
                ]}
            />
            <Typography fontSize={11} color="text.secondary" mb={1.5}>
                Glissez-déposez les cartes pour réorganiser les priorités automatiquement.
                {reordering ? " Enregistrement..." : ""}
            </Typography>
            <Grid container spacing={2}>
                {paginated.map((r) => {
                    const hasConditions = r.conditions && Object.keys(r.conditions).length > 0;
                    const actionsCount = Array.isArray(r.actions) ? r.actions.length : 0;
                    const transactionTypes = Array.isArray(r.transaction_types) && r.transaction_types.length > 0
                        ? r.transaction_types
                        : [r.transaction_type].filter(Boolean);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={r.id}>
                            {(() => {
                                const cardCursor = reordering ? "default" : (draggingRuleId === r.id ? "grabbing" : "grab");
                                return (
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.rules.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.rules.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                cursor: cardCursor,
                                userSelect: "none",
                                opacity: draggingRuleId === r.id ? 0.7 : 1,
                                "& *": { cursor: "inherit" },
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.rules.main}30`,
                                },
                            }}
                                draggable={!reordering}
                                onDragStart={handleDragStart(r.id)}
                                onDragOver={handleDragOver(r.id)}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                            >
                                {/* Priority badge */}
                                <Tooltip title="Priorité" placement="left">
                                    <Box sx={{
                                        position: "absolute", top: 10, right: 10,
                                        width: 28, height: 28, borderRadius: "50%",
                                        background: SECTION_COLORS.rules.gradient,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxShadow: `0 2px 8px ${SECTION_COLORS.rules.main}40`, cursor: "default",
                                    }}>
                                        <Typography fontSize={11} fontWeight={800} color="#fff">{r.priority ?? 0}</Typography>
                                    </Box>
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={8}>
                                    <GavelIcon sx={{ color: SECTION_COLORS.rules.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {r.name || r.transaction_type}
                                    </Typography>
                                </Box>
                                <Typography fontSize={11} color="text.secondary" noWrap mb={1.5} sx={{ display: 'block', pl: 3.5 }}>
                                    Transactions: {transactionTypes.join(", ")}
                                </Typography>
                                {/* Meta chips */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {hasConditions && (
                                        <Chip label="Conditions" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fffbeb", color: "#92400e" }} />
                                    )}
                                    {actionsCount > 0 && (
                                        <Chip label={`${actionsCount} action${actionsCount > 1 ? "s" : ""}`} size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                    )}
                                </Box>

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={r.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(r)}
                                                sx={{
                                                    cursor: "pointer",
                                                    color: SECTION_COLORS.rules.main, bgcolor: `${SECTION_COLORS.rules.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.rules.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(r)}
                                                sx={{ cursor: "pointer", color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(r)}
                                                sx={{ cursor: "pointer", color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                                );
                            })()}
                        </Grid>
                    );
                })}
            </Grid>

            {
                filteredRules.length > ROWS_PER_PAGE && (
                    <TablePagination component="div" count={filteredRules.length} page={page}
                        onPageChange={handlePageChange(3)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                        sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
                )
            }
        </Box >
    );
};

export default RulesTab;
